import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { usePresence } from '../hooks/usePresence'
import { menuSize, placeAnchoredMenu } from '../utils/menuPlacement'
import './AdminFilterSelect.css'

export default function AdminFilterSelect({
  id,
  label,
  value,
  options = [],
  onChange,
  disabled = false,
  autoOpen = false,
  className = '',
}) {
  const [open, setOpen] = useState(autoOpen)

  useEffect(() => {
    if (!autoOpen) return undefined
    const timer = window.setTimeout(() => setOpen(true), 50)
    return () => window.clearTimeout(timer)
  }, [autoOpen])
  const [coords, setCoords] = useState({ left: 0, top: 0, width: 220 })
  const [activeIndex, setActiveIndex] = useState(0)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const frostRef = useRef(null)
  const optionRefs = useRef([])
  const reducedMotion = usePrefersReducedMotion()
  const { mounted, leaving } = usePresence(open, { duration: 180, reduced: reducedMotion })
  const listId = useId()
  const selected = options.find((option) => option.value === value) || options[0]
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === value))

  useLayoutEffect(() => {
    if (!mounted) return undefined
    const update = () => {
      const trigger = triggerRef.current?.getBoundingClientRect()
      if (!trigger) return
      const size = menuSize(menuRef.current)
      const width = Math.max(trigger.width, size.width, 180)
      const placed = placeAnchoredMenu({
        trigger,
        width,
        height: size.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
        align: 'start',
        prefer: 'below',
      })
      setCoords((prev) => (
        prev.left === placed.left && prev.top === placed.top && prev.width === width
          ? prev
          : { ...placed, width }
      ))
      if (frostRef.current) {
        void getComputedStyle(frostRef.current).backdropFilter
      }
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [mounted, options, value])

  useEffect(() => {
    if (open) setActiveIndex(selectedIndex)
  }, [open, selectedIndex])

  useEffect(() => {
    if (!mounted) return undefined
    optionRefs.current[activeIndex]?.focus()
  }, [mounted, activeIndex])

  useEffect(() => {
    if (!mounted) return undefined
    const onPointer = (event) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKey = (event) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [mounted])

  const move = (delta) => {
    if (!options.length) return
    setActiveIndex((index) => (index + delta + options.length) % options.length)
  }

  const choose = (next) => {
    onChange?.(next)
    setOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <div className={`admin-filter${className ? ` ${className}` : ''}`}>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className={`admin-filter-trigger${open ? ' is-open' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={mounted ? listId : undefined}
        aria-label={label}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setOpen(true)
          }
        }}
      >
        <span className="admin-filter-value">{selected?.label || label}</span>
        <span className="admin-filter-caret" aria-hidden="true" />
      </button>
      {mounted && typeof document !== 'undefined' ? createPortal(
        <div className="admin-filter-root" style={{ left: coords.left, top: coords.top, width: coords.width }}>
          <div className="lg-blur-prewarm" aria-hidden="true" />
          <div
            ref={menuRef}
            className={`admin-filter-menu lg-panel${leaving ? ' is-leaving' : ''}`}
            role="listbox"
            id={listId}
            aria-label={label}
          >
            <div className="lg-panel-frost" ref={frostRef} aria-hidden="true" />
            <div className="lg-panel-tint" aria-hidden="true" />
            <div className="lg-panel-shine" aria-hidden="true" />
            <div className={`lg-panel-body admin-filter-motion${reducedMotion ? '' : leaving ? ' is-leaving' : ' is-animated'}`}>
              {options.map((option, index) => {
                const isSelected = option.value === value
                return (
                  <button
                    key={String(option.value)}
                    ref={(node) => { optionRefs.current[index] = node }}
                    type="button"
                    role="option"
                    tabIndex={index === activeIndex ? 0 : -1}
                    aria-selected={isSelected}
                    className={`admin-filter-option${isSelected ? ' is-selected' : ''}${index === activeIndex ? ' is-active' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(option.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowDown') {
                        event.preventDefault()
                        move(1)
                      } else if (event.key === 'ArrowUp') {
                        event.preventDefault()
                        move(-1)
                      } else if (event.key === 'Home') {
                        event.preventDefault()
                        setActiveIndex(0)
                      } else if (event.key === 'End') {
                        event.preventDefault()
                        setActiveIndex(options.length - 1)
                      } else if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        choose(option.value)
                      }
                    }}
                  >
                    <span>{option.label}</span>
                    {isSelected ? <span className="admin-filter-check" aria-hidden="true">✓</span> : null}
                  </button>
                )
              })}
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}
